import React, { useEffect, useState, useMemo } from 'react';
import MUIDataTable from 'mui-datatables';
import axios from 'axios';
import config from '../config/config';
import {
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  AccountBalance as BalanceIcon,
  Payments as PaymentsIcon,
  MoneyOff as MoneyOffIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Expenses = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const navigate = useNavigate();

  const fetchExpenses = async (page = 0, rowsPerPage = 100, searchText = '') => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const res = await axios.get(config.API_URL + '/api/pullexpenses', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: page + 1,
          per_page: rowsPerPage,
          search: searchText,
        },
      });

      setData(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses(page, rowsPerPage, searchText);
  }, [page, rowsPerPage, searchText]);

  const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 2
  }).format(amount);
};

  // Calculate financial metrics
  const stats = useMemo(() => {
    const totalAmount = data.reduce((sum, expense) => sum + (Number(expense.grand_total) || 0), 0);
    const totalPaid = data.reduce((sum, expense) => sum + (Number(expense.paid_amount) || 0), 0);
    const totalBalance = data.reduce((sum, expense) => sum + (Number(expense.grand_total-expense.paid_amount) || 0), 0);

    return {
      totalAmount: totalAmount,
      totalPaid: totalPaid,
      totalBalance: totalBalance,
    };
  }, [data]);

  // Premium Minimalistic Widget Component
  const PremiumWidget = ({ title, value, icon, color, subtitle }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        backgroundColor: '#ffffff',
        borderRadius: 2,
        border: '1px solid #f0f0f0',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e8e8e8',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666666',
                fontWeight: 500,
                fontSize: '0.875rem',
                mb: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1a1a1a',
                fontSize: '1.75rem',
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: '#999999',
                  fontSize: '0.8rem',
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: color,
              width: 48,
              height: 48,
              ml: 2,
              boxShadow: 'none',
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const handleMenuClick = (event, expense) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedExpense(expense);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    setOpenModal(true);
    handleMenuClose();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleRecognizeAsset = (id) => {
    
    //navigate(`/Assets_/${id}`);
  }

  const columns = useMemo(
    () => [
      { name: 'id', label: 'ID' },
      { name: 'note', label: 'Name' },
      {
        name: 'grand_total',
        label: 'Amount',
        options: {
          customBodyRender: (value) => (value ? `${formatCurrency(value)}` : '0.00'),
        },
      },
      {
        name: 'paid_amount',
        label: 'Paid',
        options: {
          customBodyRender: (value) => (value ? `${formatCurrency(value)}` : '0.00'),
        },
      },
      {
        name: 'balance',
        label: 'Balance',
        options: {
          customBodyRender: (value) => (value ? `${formatCurrency(value)}` : '0.00'),
        },
      },
      { name: 'created_at', label: 'Created On' },
      {
        name: 'actions',
        label: 'Actions',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value, tableMeta) => {
            const expense = data[tableMeta.rowIndex];
            return (
              <IconButton
                onClick={(e) => handleMenuClick(e, expense)}
                size="small"
                sx={{
                  color: '#666666',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            );
          },
        },
      },
    ],
    [data]
  );

  const options = useMemo(
    () => ({
      search: false,
      filter: true,
      download: true,
      print: true,
      pagination: true,
      serverSide: true,
      count: total,
      rowsPerPage: rowsPerPage,
      page: page,
      elevation: 0,
      selectableRows: 'none',
      responsive: 'standard',
      tableBodyHeight: 'auto',
      onTableChange: (action, tableState) => {
        switch (action) {
          case 'changePage':
            setPage(tableState.page);
            break;
          case 'changeRowsPerPage':
            setRowsPerPage(tableState.rowsPerPage);
            setPage(0);
            break;
          default:
            break;
        }
      },
      onRowClick: (rowData, rowMeta) => {
        const expense = data[rowMeta.dataIndex];
        navigate(`/viewexpense/${expense.id}`);
      },
      customFooter: () => (
        <tfoot>
          <tr style={{ backgroundColor: '#fafafa' }}>
           <td  style={{ 
              textAlign: 'right', 
              padding: '16px', 
              fontWeight: 600,
              color: '#333333',
              fontSize: '0.875rem',
            }}>
             
            </td>
            <td  style={{ 
              textAlign: 'right', 
              padding: '16px', 
              fontWeight: 600,
              color: '#333333',
              fontSize: '0.875rem',
            }}>
             
            </td>
            <td style={{ 
              padding: '16px', 
              fontWeight: 600,
              color: '#333333',
              fontSize: '0.875rem',
            }}>
             
            </td>
            <td style={{ 
              padding: '16px', 
              fontWeight: 600,
              color: '#4caf50',
              fontSize: '0.875rem',
            }}>
              
            </td>
            <td style={{ 
              padding: '16px', 
              fontWeight: 600,
              color: '#e91e63',
              fontSize: '0.875rem',
            }}>
              
            </td>
           <td  style={{ 
              textAlign: 'right', 
              padding: '16px', 
              fontWeight: 600,
              color: '#333333',
              fontSize: '0.875rem',
            }}>
             
            </td>
            <td  style={{ 
              textAlign: 'right', 
              padding: '16px', 
              fontWeight: 600,
              color: '#333333',
              fontSize: '0.875rem',
            }}>
             
            </td>
            <td  style={{ 
              textAlign: 'right', 
              padding: '16px', 
              fontWeight: 600,
              color: '#333333',
              fontSize: '0.875rem',
            }}>
             
            </td>
            <td  style={{ 
              textAlign: 'right', 
              padding: '16px', 
              fontWeight: 600,
              color: '#333333',
              fontSize: '0.875rem',
            }}>
             
            </td>
          </tr>
        </tfoot>
      ),
      customTableBodyFooterRender: () => null,
      textLabels: {
        body: {
          noMatch: loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            "No matching records found"
          ),
        },
      },
    }),
    [total, page, rowsPerPage, data, navigate, stats, loading]
  );

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3 }, 
      backgroundColor: '#ffffff', 
      minHeight: '100vh',
      '& .MUIDataTable-root': {
        backgroundColor: '#ffffff',
        boxShadow: 'none',
        border: '1px solid #f0f0f0',
        borderRadius: 2,
      },
      '& .MUIDataTable-paper': {
        boxShadow: 'none',
        backgroundColor: '#ffffff',
      },
      '& .MuiTableHead-root': {
        backgroundColor: '#fafafa',
        '& .MuiTableCell-head': {
          color: '#666666',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          borderBottom: '1px solid #f0f0f0',
        },
      },
      '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
          '&:hover': {
            backgroundColor: '#f9f9f9',
            cursor: 'pointer',
          },
          '&:not(:last-child)': {
            borderBottom: '1px solid #f5f5f5',
          },
        },
        '& .MuiTableCell-body': {
          fontSize: '0.875rem',
          color: '#333333',
          borderBottom: 'none',
          padding: '12px 16px',
        },
      },
      '& .MUIDataTable-toolbar': {
        backgroundColor: '#ffffff',
        paddingLeft: 0,
        '& h6': {
          color: '#1a1a1a',
          fontWeight: 600,
          fontSize: '1.25rem',
        },
      },
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600, 
            color: '#1a1a1a',
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2rem' },
          }}
        >
          Expenses
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#666666',
            fontSize: '0.95rem',
          }}
        >
          Track and manage your business expenses
        </Typography>
      </Box>

      {/* Stats Widgets */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <PremiumWidget
            title="Total Amount"
            value={formatCurrency(stats.totalAmount)}
            icon={<BalanceIcon sx={{ fontSize: 24 }} />}
            color="#2196f3"
            subtitle="Total expense amount"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <PremiumWidget
            title="Total Paid"
            value={formatCurrency(stats.totalPaid)}
            icon={<PaymentsIcon sx={{ fontSize: 24 }} />}
            color="#4caf50"
            subtitle="Total amount paid"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <PremiumWidget
            title="Outstanding"
            value={formatCurrency(stats.totalBalance)}
            icon={<MoneyOffIcon sx={{ fontSize: 24 }} />}
            color="#e91e63"
            subtitle="Total balance due"
          />
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          placeholder="Search expenses by name..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', sm: '400px' },
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              borderRadius: 2,
              '& fieldset': {
                borderColor: '#f0f0f0',
              },
              '&:hover fieldset': {
                borderColor: '#e0e0e0',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#2196f3',
              },
            },
            '& .MuiInputBase-input': {
              padding: '12px 14px',
              fontSize: '0.875rem',
            },
          }}
        />
      </Box>

      {/* Data Table */}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          py: 8,
          backgroundColor: '#ffffff',
          borderRadius: 2,
          border: '1px solid #f0f0f0',
        }}>
          <CircularProgress size={32} sx={{ color: '#2196f3' }} />
        </Box>
      ) : (
        <MUIDataTable 
          title="" 
          data={data} 
          columns={columns} 
          options={options} 
        />
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            border: '1px solid #f0f0f0',
            borderRadius: 2,
            mt: 1,
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              color: '#333333',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleView}>View Details</MenuItem>
      </Menu>

      {/* Details Modal */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: '1px solid #f0f0f0',
            minWidth: { xs: '90%', sm: '500px' },
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          color: '#1a1a1a',
          fontWeight: 600,
          fontSize: '1.25rem',
        }}>
          Expense Details
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedExpense && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666666', fontWeight: 500 }}>
                  ID
                </Typography>
                <Typography variant="body2" sx={{ color: '#333333' }}>
                  {selectedExpense.id}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666666', fontWeight: 500 }}>
                  Name
                </Typography>
                <Typography variant="body2" sx={{ color: '#333333' }}>
                  {selectedExpense.note}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666666', fontWeight: 500 }}>
                  Amount
                </Typography>
                <Typography variant="body2" sx={{ color: '#333333', fontWeight: 600 }}>
                  {formatCurrency(selectedExpense.grand_total) || '0.00'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666666', fontWeight: 500 }}>
                  Paid
                </Typography>
                <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                  {formatCurrency(selectedExpense.paid_amount) || '0.00'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666666', fontWeight: 500 }}>
                  Balance
                </Typography>
                <Typography variant="body2" sx={{ color: '#e91e63', fontWeight: 600 }}>
                  {formatCurrency(selectedExpense.grand_total-selectedExpense.paid_amount) || '0.00'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666666', fontWeight: 500 }}>
                  Created On
                </Typography>
                <Typography variant="body2" sx={{ color: '#333333' }}>
                  {selectedExpense.created_at}
                </Typography>
              </Box>
           
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
       {/*  <Button 
            onClick={(e) => handleRecognizeAsset(selectedExpense.id)}
            variant="outlined"
            sx={{
              borderColor: 'green',
              backgroundColor: 'green',
              color: '#ffff',
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'green',
                borderColor: 'green',
              },
            }}
          >
            Recognise as asset
          </Button>*/}
          <Button 
            onClick={handleCloseModal}
            variant="outlined"
            sx={{
              borderColor: '#f0f0f0',
              color: '#666666',
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#e0e0e0',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Expenses;